import { useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, FileText, Star, Upload, Video, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTE_NAMES } from '@tsgecart/config';
import { AdminLoadingState } from '../../components/primitives/admin-loading-state.js';
import { AdminEmptyState } from '../../components/primitives/admin-empty-state.js';
import { AdminErrorState } from '../../components/primitives/admin-error-state.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { AdminApiError } from '../../api/admin-client.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import {
  useAdminMediaLibrary,
  useProductImages,
  useProductImageMutations,
  useUploadMedia,
} from '../../media/hooks/use-media.js';

export interface ProductMediaTabProps {
  productId: string;
}

/**
 * No drag-and-drop reordering (Part 13 explicitly defers it -- "do not
 * implement unsafe drag-and-drop hierarchy/ordering updates unless
 * transactions and validation are correct"). Move-left/move-right buttons
 * call the same `reorderImages` endpoint with the full recomputed order,
 * which is exactly as correct and far simpler to get right.
 */
export function ProductMediaTab({ productId }: ProductMediaTabProps) {
  const { data: images, isLoading, isError, refetch } = useProductImages(productId);
  const { reorder, setPrimary, unlink, link } = useProductImageMutations(productId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKind, setPickerKind] = useState<'image' | 'video'>('image');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data: library, isLoading: libraryLoading } = useAdminMediaLibrary({
    search: debouncedSearch || undefined,
    kind: pickerKind,
    limit: 20,
  });
  const upload = useUploadMedia();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (isLoading) return <AdminLoadingState label="Loading media" />;
  if (isError) return <AdminErrorState message="Media could not be loaded." onRetry={() => void refetch()} />;

  const rows = images ?? [];
  const linkedIds = new Set(rows.map((r) => r.mediaAssetId));

  function move(index: number, direction: -1 | 1) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= rows.length) return;
    const ids = rows.map((r) => r.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(targetIndex, 0, moved!);
    reorder.mutate(ids);
  }

  function handleFileSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadError(null);
    upload.mutate(file, {
      onSuccess: (asset) => link.mutate({ mediaAssetId: asset.id }),
      onError: (error) =>
        setUploadError(error instanceof AdminApiError ? error.message : 'Could not upload file.'),
    });
  }

  return (
    <div className="space-y-4">
      {uploadError && (
        <p className="text-sm text-red-600" role="alert">
          {uploadError}
        </p>
      )}
      {rows.length === 0 ? (
        <AdminEmptyState title="No media linked" description="Link an existing asset or upload a new one below." />
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {rows.map((image, index) => (
            <li key={image.id} className="relative overflow-hidden rounded-xl border border-gray-100">
              {image.mimeType.startsWith('video/') ? (
                image.url ? (
                  <video src={image.url} muted className="h-28 w-full object-cover" />
                ) : (
                  <div className="flex h-28 w-full items-center justify-center bg-gray-100 text-gray-400">
                    <Video className="h-6 w-6" aria-hidden="true" />
                  </div>
                )
              ) : image.url ? (
                <img src={image.url} alt={image.altText ?? ''} className="h-28 w-full object-cover" />
              ) : (
                <div className="h-28 w-full bg-gray-100" aria-hidden="true" />
              )}
              {image.isPrimary && (
                <span className="bg-brand text-brand-foreground absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold">
                  <Star className="h-3 w-3" aria-hidden="true" /> Primary
                </span>
              )}
              <div className="flex items-center justify-between gap-1 bg-white p-1.5">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  aria-label="Move earlier"
                  className="rounded p-1 hover:bg-gray-100 disabled:opacity-30"
                >
                  <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
                {!image.isPrimary && (
                  <button
                    type="button"
                    onClick={() => setPrimary.mutate(image.id)}
                    className="rounded p-1 hover:bg-gray-100"
                    aria-label="Set as primary"
                  >
                    <Star className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => unlink.mutate(image.id)}
                  className="rounded p-1 text-red-600 hover:bg-red-50"
                  aria-label="Unlink image"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === rows.length - 1}
                  aria-label="Move later"
                  className="rounded p-1 hover:bg-gray-100 disabled:opacity-30"
                >
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pickerOpen ? (
        <div className="rounded-xl border border-gray-100 p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <AdminSearchInput value={search} onChange={setSearch} placeholder="Search media library…" />
            <div className="flex items-center gap-1 rounded-full border border-gray-200 p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setPickerKind('image')}
                className={`rounded-full px-2.5 py-1 ${pickerKind === 'image' ? 'bg-gray-900 text-white' : 'text-gray-500'}`}
              >
                Images
              </button>
              <button
                type="button"
                onClick={() => setPickerKind('video')}
                className={`rounded-full px-2.5 py-1 ${pickerKind === 'video' ? 'bg-gray-900 text-white' : 'text-gray-500'}`}
              >
                Videos
              </button>
            </div>
            <button
              type="button"
              onClick={() => setPickerOpen(false)}
              className="rounded-full px-3 py-1 text-sm text-gray-500 hover:bg-gray-100"
            >
              Close
            </button>
          </div>
          {libraryLoading ? (
            <AdminLoadingState label="Loading media library" />
          ) : (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {(library ?? []).map((asset) => {
                const alreadyLinked = linkedIds.has(asset.id);
                return (
                  <button
                    key={asset.id}
                    type="button"
                    disabled={alreadyLinked || link.isPending}
                    onClick={() => link.mutate({ mediaAssetId: asset.id })}
                    className="overflow-hidden rounded-lg border border-gray-100 disabled:opacity-40"
                    title={alreadyLinked ? 'Already linked to this product' : `Link this ${asset.kind}`}
                  >
                    {asset.kind === 'video' ? (
                      asset.url ? (
                        <video src={asset.url} muted className="h-16 w-full object-cover" />
                      ) : (
                        <div className="flex h-16 w-full items-center justify-center bg-gray-100 text-gray-400">
                          <Video className="h-4 w-4" aria-hidden="true" />
                        </div>
                      )
                    ) : asset.url ? (
                      <img src={asset.url} alt="" className="h-16 w-full object-cover" />
                    ) : (
                      <div className="h-16 w-full bg-gray-100" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="focus-visible:ring-brand rounded-full border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
          >
            Link from the media library
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,video/mp4"
            className="hidden"
            onChange={handleFileSelected}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={upload.isPending}
            className="focus-visible:ring-brand inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-60"
          >
            <Upload className="h-3.5 w-3.5" aria-hidden="true" />
            {upload.isPending ? 'Uploading…' : 'Upload new'}
          </button>
          <Link
            to={ROUTE_NAMES.adminMedia}
            className="text-brand inline-flex items-center gap-1 text-sm hover:underline"
          >
            <FileText className="h-3.5 w-3.5" aria-hidden="true" />
            Open media library
          </Link>
        </div>
      )}
    </div>
  );
}
